import checkStatementPeriodically from '~/lib/onload/check-statement-periodically'

const ONE_MINUTE_IN_MS = 60000

let eventsQueue = []
let bloomreachInitializedTime = null

export default (context, inject) => {
  const bloomreachToken = context.$config.bloomreachToken

  inject('bloomreach', {
    /**
     * @param {string} eventName
     * @param {Array<*>} args
     * @returns {*}
     */
    trackEvent({ eventName, args }) {
      if (window.exponea) {
        return window.exponea[eventName](...args)
      }

      // Events get saved for a maximum of 1 minute in a queue
      // so the events don't get lost.
      const currentTime = new Date().getTime()
      const trackEventsInQueue = currentTime < bloomreachInitializedTime + ONE_MINUTE_IN_MS

      if (trackEventsInQueue) {
        eventsQueue.push({ eventName, args })
      }
    },

    /**
     * @param {string} emailAddress
     */
    identifyUser(emailAddress) {
      this.trackEvent({
        eventName: 'identify',
        args: [
          {
            email_id: emailAddress,
          },
        ],
      })
    },

    /**
     * @param {string} tag
     * @param {string|null} emailAddress
     */
    newsletterConsent(tag, emailAddress = null) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'consent',
          {
            action: 'accept',
            category: 'newsletter',
            valid_until: 'unlimited',
            timestamp: new Date().getTime(),
            email: emailAddress,
            language: context.i18n?.locale,
            tag,
          },
        ],
      })
    },

    /**
     * On create and update
     * @param {loavies.models.listing.ListingModel} listing
     */
    listingUpdate(listing) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'reloaved_listing',
          {
            product_sku: parentSkuFromVariantSku(listing.publicData.magentoSku),
            variant_sku: listing.publicData.magentoSku,
            title: listing.title,
            product_group: listing.publicData.productGroup,
            local_currency: listing.price.currency,
            price: formatCentsToFloat(listing.price.amount),
            store: listing.author.publicData.storeName,
            listing_status: listing.state,
          },
        ],
      })
    },

    /**
     * On review of transaction
     * @param {loavies.models.transaction.TransactionModel} transaction
     * @param {number} rating
     */
    reviewed(transaction, rating) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'reloaved_review',
          {
            price: formatCentsToFloat(transaction.payoutTotal.amount),
            store: transaction.provider.publicData.storeName,
            category_level_1: transaction.listing.publicData.productGroup,
            purchase_id: transaction.id,
            variant_sku_list: [transaction.listing.publicData.magentoSku],
            product_sku_list: [parentSkuFromVariantSku(transaction.listing.publicData.magentoSku)],
            total_quantity: [transaction.lineItems.length - 1], // minus delivery costs
            total_price: formatCentsToFloat(transaction.payinTotal.amount),
            stars: rating,
          },
        ],
      })
    },

    /**
     * On opening main category
     * @param {string} categoryName
     */
    viewCategory(categoryName) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'reloaved_view_category',
          {
            category_level_1: categoryName,
          },
        ],
      })
    },

    /**
     * On viewing listing
     * @param {loavies.models.listing.ListingModel} listing
     */
    viewItem(listing) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'reloaved_view_item',
          {
            product_group: listing.publicData.productGroup,
            title: listing.title,
            price: formatCentsToFloat(listing.price.amount),
            product_sku: parentSkuFromVariantSku(listing.publicData.magentoSku),
            variant_sku: listing.publicData.magentoSku,
          },
        ],
      })
    },

    /**
     * On creating and updating wallet
     * @param {loavies.models.wallet.WalletModel} wallet
     * @param {'activated'|'payout'} action
     */
    walletUpdate(wallet, action) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'reloaved_wallet',
          {
            action,
            amount: formatCentsToFloat(wallet.balance),
          },
        ],
      })
    },

    /**
     * On updating wishlist with add or remove
     * @param {loavies.models.listing.ListingModel} listing
     * @param {'add'|'remove'} action
     * @param {loavies.models.listing.ListingModel[]} wishlist
     */
    wishlistUpdate(listing, action, wishlist) {
      this.trackEvent({
        eventName: 'track',
        args: [
          'reloaved_wishlist',
          {
            action,
            product_group: listing.publicData.productGroup,
            price: formatCentsToFloat(listing.price.amount),
            product_sku: parentSkuFromVariantSku(listing.publicData.magentoSku),
            variant_sku: listing.publicData.magentoSku,
            store: listing.author.publicData.storeName,
            product_skus: wishlist.map(listing => parentSkuFromVariantSku(listing.publicData.magentoSku)),
            variant_skus: wishlist.map(listing => listing.publicData.magentoSku),
          },
        ],
      })
    },
  })

  initiateBloomreachScript(bloomreachToken)
}

/**
 * @param {string} variantSku
 * @return {string}
 */
function parentSkuFromVariantSku(variantSku) {
  if (!variantSku) {
    return
  }

  return `${variantSku.slice(0, -2)}00`
}

/**
 * @param {number} cents
 * @return {string}
 */
function formatCentsToFloat(cents) {
  return parseFloat(String(cents / 100)).toFixed(2)
}

/**
 * Docs: https://documentation.bloomreach.com/engagement/docs/js-sdk
 * Script gets started when script is done loading
 */
function initiateBloomreachScript(bloomreachToken) {
  bloomreachInitializedTime = new Date().getTime()

  // eslint-disable-next-line
  !function(e,n,t,i,o,r){function c(e){if("number"!=typeof e)return e;var n=new Date;return new Date(n.getTime()+1e3*e)}var a=4e3,s="xnpe_async_hide";function p(e){return e.reduce((function(e,n){return e[n]=function(){e._.push([n.toString(),arguments])},e}),{_:[]})}function m(e,n,t){var i=t.createElement(n);i.src=e;var o=t.getElementsByTagName(n)[0];return o.parentNode.insertBefore(i,o),i}function u(e){return"[object Date]"===Object.prototype.toString.call(e)}r.target=r.target||"https://api.exponea.com",r.file_path=r.file_path||r.target+"/js/exponea.min.js",o[n]=p(["anonymize","initialize","identify","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers"]),o[n].notifications=p(["isAvailable","isSubscribed","subscribe","unsubscribe"]),o[n]["snippetVersion"]="v2.3.0",function(e,n,t){e[n]["_"+t]={},e[n]["_"+t].nowFn=Date.now,e[n]["_"+t].snippetStartTime=e[n]["_"+t].nowFn()}(o,n,"performance"),function(e,n,t,i,o,r){e[o]={sdk:e[i],sdkObjectName:i,skipExperiments:!!t.new_experiments,sign:t.token+"/"+(r.exec(n.cookie)||["","new"])[1],path:t.target}}(o,e,r,n,i,RegExp("__exponea_etc__"+"=([\\w-]+)")),function(e,n,t){m(e.file_path,n,t)}(r,t,e),function(e,n,t,i,o,r,p){if(e.new_experiments){!0===e.new_experiments&&(e.new_experiments={});var f,l=e.new_experiments.hide_class||s,_=e.new_experiments.timeout||a,d=encodeURIComponent(r.location.href.split("#")[0]);e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||u(e.cookies.expires)?f=c(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||u(e.cookies.expires.tracking))&&(f=c(e.cookies.expires.tracking))),f&&f<new Date&&(f=void 0);var x=e.target+"/webxp/"+n+"/"+r[t].sign+"/modifications.min.js?http-referer="+d+"&timeout="+_+"ms"+(f?"&cookie-expires="+Math.floor(f.getTime()/1e3):"");"sync"===e.new_experiments.mode&&r.localStorage.getItem("__exponea__sync_modifications__")?function(e,n,t,i,o){t[o][n]="<"+n+' src="'+e+'"></'+n+">",i.writeln(t[o][n]),i.writeln("<"+n+">!"+o+".init && document.writeln("+o+"."+n+'.replace("/'+n+'/", "/'+n+'-async/").replace("><", " async><"))</'+n+">")}(x,n,r,p,t):function(e,n,t,i,o,r,c,a){r.documentElement.classList.add(e);var s=m(t,i,r);function p(){o[a].init||m(t.replace("/"+i+"/","/"+i+"-async/"),i,r)}function u(){r.documentElement.classList.remove(e)}s.onload=p,s.onerror=p,o.setTimeout(u,n),o[c]._revealPage=u}(l,_,x,n,r,p,o,t)}}(r,t,i,0,n,o,e),function(e,n,t){e[n].start=function(i){i&&Object.keys(i).forEach((function(e){return t[e]=i[e]})),e[n].initialize(t)}}(o,n,r)}(document,"exponea","script","webxpClient",window,{
    target: 'https://api.eu1.exponea.com',
    token: bloomreachToken,
    track: {
      visits: true,
      google_analytics: false,
    },
  })

  checkStatementPeriodically({
    statement: () => !!window.exponea,
    callback: startBloomreachTracking,
  })
}

function startBloomreachTracking() {
  // Bloomreach gets started here
  window.exponea.start()

  if (eventsQueue.length > 0) {
    eventsQueue.forEach(queuedEvent => window.exponea[queuedEvent.eventName](...queuedEvent.args))
    eventsQueue = []
  }
}
